import { useState, useCallback, useEffect } from 'react';
import { Text, Button, Flex, Spinner } from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import { toast } from 'sonner';
import styles from './VerifySnippetDialog.module.scss';

interface EditCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionName: string;
  collectionPath: string;
  onUpdateCollection: (newName: string) => Promise<void>;
}

export function EditCollectionDialog({
  open,
  onOpenChange,
  collectionName,
  collectionPath,
  onUpdateCollection,
}: EditCollectionDialogProps) {
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setNewCollectionName(collectionName);
    }
  }, [open, collectionName]);

  const handleUpdate = useCallback(async () => {
    if (newCollectionName.trim() && newCollectionName.trim() !== collectionName && !isLoading) {
      setIsLoading(true);
      try {
        await onUpdateCollection(newCollectionName.trim());
        toast.success('Collection name updated successfully');
        onOpenChange(false);
      } catch (error) {
        toast.error('Failed to update collection name. Please try again.');
        console.error('Error updating collection name:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [newCollectionName, collectionName, isLoading, onUpdateCollection, onOpenChange]);

  const handleCancel = useCallback(() => {
    if (!isLoading) {
      setNewCollectionName(collectionName);
      onOpenChange(false);
    }
  }, [isLoading, collectionName, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === 'Enter' && newCollectionName.trim() && newCollectionName.trim() !== collectionName && !isLoading) {
        e.preventDefault();
        handleUpdate();
      }
    },
    [newCollectionName, collectionName, isLoading, handleUpdate]
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !isLoading && onOpenChange(open)}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} disabled={isLoading} />
        <Flex direction="column" gap="6" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              Edit Collection Name
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Update the name of the collection "{collectionName}".
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Collection name</Text>
            </label>
            <input
              type="text"
              className={styles.input}
              value={newCollectionName}
              onChange={(e) => {
                e.stopPropagation();
                setNewCollectionName(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="Enter collection name"
              disabled={isLoading}
              autoFocus
            />
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              onClick={handleUpdate}
              disabled={!newCollectionName.trim() || newCollectionName.trim() === collectionName || isLoading}
              className={styles.verifyButton}
            >
              {isLoading ? (
                <>
                  <Spinner size="1" />
                  <Text size="3">Updating...</Text>
                </>
              ) : (
                <Text size="3">Update</Text>
              )}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
